import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import './App.css';
import SidebarNew from './components/SidebarNew';
import NavbarNew from './components/NavbarNew';
import FooterNew from './components/FooterNew';
import HomePageNew from './pages/HomePageNew';
import AboutPageNew from './pages/AboutPageNew';
import ProjectsPageNew from './pages/ProjectsPageNew';
import BlogPageNew from './pages/BlogPageNew';
import BlogContentPageNew from './pages/BlogContentPageNew';
import ProjectDetailPageNew from './pages/ProjectDetailPageNew';
import ClientsPageNew from './pages/ClientsPageNew';
import OtherVenturesPageNew from './pages/OtherVenturesPageNew';
import ContactPageNew from './pages/ContactPageNew';
import { adminLogout, isAdminAuthenticated } from './services/adminApi';

const AdminApp = lazy(() => import('./admin/AdminApp'));

const ADMIN_VIEWS = new Set([
  'admin_login',
  'admin_dashboard',
  'admin_blogs',
  'admin_blog_form',
  'admin_contacts',
]);

function getViewUrl(viewId, projectId, articleId, blogId) {
  switch (viewId) {
    case 'home':
      return '/';
    case 'about_page':
      return '/about';
    case 'projects_page':
      return '/projects';
    case 'project-detail':
      return `/projects?id=${projectId}`;
    case 'blog':
      return '/blog';
    case 'blog-content':
      return `/blog?id=${articleId}`;
    case 'clients_page':
      return '/clients';
    case 'ventures_page':
      return '/ventures';
    case 'contact_page':
      return '/contact';
    case 'admin_login':
      return '/admin/login';
    case 'admin_dashboard':
      return '/admin/dashboard';
    case 'admin_blogs':
      return '/admin/blogs';
    case 'admin_blog_form':
      return blogId ? `/admin/blogs/edit?id=${blogId}` : '/admin/blogs/new';
    case 'admin_contacts':
      return '/admin/contacts';
    default:
      return '/';
  }
}

function getSidebarStateForView(viewId) {
  switch (viewId) {
    case 'home':
      return { sidebarLabel: 'ARCHITECTURE &nbsp; • &nbsp; DESIGN', scrollCounter: '01' };
    case 'about_page':
      return { sidebarLabel: 'MANIFESTO &nbsp; • &nbsp; LEGACY', scrollCounter: '03' };
    case 'projects_page':
      return { sidebarLabel: 'ARCHITECTURAL INDEX', scrollCounter: '02' };
    case 'blog':
      return { sidebarLabel: 'EDITORIAL &nbsp; • &nbsp; PUBLICATIONS', scrollCounter: '04' };
    case 'blog-content':
      return { sidebarLabel: 'INSIGHTS &nbsp; • &nbsp; KNOWLEDGE', scrollCounter: '—' };
    case 'clients_page':
      return { sidebarLabel: 'PARTNERS &nbsp; • &nbsp; COLLABORATORS', scrollCounter: '05' };
    case 'ventures_page':
      return { sidebarLabel: 'VENTURES &nbsp; • &nbsp; EXPANSION', scrollCounter: '06' };
    case 'contact_page':
      return { sidebarLabel: 'INQUIRY &nbsp; • &nbsp; COLLABORATION', scrollCounter: '07' };
    case 'project-detail':
      return { sidebarLabel: 'CASE STUDY &nbsp; • &nbsp; GALLERY', scrollCounter: '✦' };
    default:
      return { sidebarLabel: 'ARCHITECTURE &nbsp; • &nbsp; DESIGN', scrollCounter: '01' };
  }
}

function parseHashToRoute() {
  const hash = window.location.hash.slice(1) || '/';
  const [path, queryString] = hash.split('?');
  const params = new URLSearchParams(queryString);

  if (path === '/admin' || path === '/admin/login') {
    return {
      viewId: isAdminAuthenticated() ? 'admin_dashboard' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: null,
    };
  }

  if (path === '/admin/dashboard') {
    return {
      viewId: isAdminAuthenticated() ? 'admin_dashboard' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: null,
    };
  }

  if (path === '/admin/blogs') {
    return {
      viewId: isAdminAuthenticated() ? 'admin_blogs' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: null,
    };
  }

  if (path === '/admin/blogs/new') {
    return {
      viewId: isAdminAuthenticated() ? 'admin_blog_form' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: null,
    };
  }

  if (path === '/admin/blogs/edit') {
    const blogId = parseInt(params.get('id'), 10);
    return {
      viewId: isAdminAuthenticated() ? 'admin_blog_form' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: blogId || null,
    };
  }

  if (path === '/admin/contacts') {
    return {
      viewId: isAdminAuthenticated() ? 'admin_contacts' : 'admin_login',
      projectId: null,
      articleId: null,
      blogId: null,
    };
  }

  if (path === '/about') {
    return { viewId: 'about_page', projectId: null, articleId: null, blogId: null };
  }

  if (path === '/projects') {
    const projectId = params.get('id');
    if (projectId) {
      return {
        viewId: 'project-detail',
        projectId: parseInt(projectId, 10),
        articleId: null,
        blogId: null,
      };
    }
    return { viewId: 'projects_page', projectId: null, articleId: null, blogId: null };
  }

  if (path === '/blog') {
    const articleId = params.get('id');
    if (articleId) {
      return {
        viewId: 'blog-content',
        projectId: null,
        articleId: parseInt(articleId, 10),
        blogId: null,
      };
    }
    return { viewId: 'blog', projectId: null, articleId: null, blogId: null };
  }

  if (path === '/clients') {
    return { viewId: 'clients_page', projectId: null, articleId: null, blogId: null };
  }

  if (path === '/ventures') {
    return { viewId: 'ventures_page', projectId: null, articleId: null, blogId: null };
  }

  if (path === '/contact') {
    return { viewId: 'contact_page', projectId: null, articleId: null, blogId: null };
  }

  return { viewId: 'home', projectId: null, articleId: null, blogId: null };
}

function createInitialAppState() {
  const route = parseHashToRoute();
  const sidebar = getSidebarStateForView(route.viewId);

  return {
    ...route,
    ...sidebar,
  };
}

function App() {
  const [initialState] = useState(() => createInitialAppState());
  const [currentView, setCurrentView] = useState(initialState.viewId);
  const currentViewRef = useRef(initialState.viewId);
  const [scrollCounter, setScrollCounter] = useState(initialState.scrollCounter);
  const [sidebarLabel, setSidebarLabel] = useState(initialState.sidebarLabel);
  const [lastViewBeforeProject, setLastViewBeforeProject] = useState('home');
  const [selectedArticleId, setSelectedArticleId] = useState(initialState.articleId);
  const [selectedProjectId, setSelectedProjectId] = useState(initialState.projectId);
  const [selectedBlogId, setSelectedBlogId] = useState(initialState.blogId);

  const showView = (
    viewId,
    sectionId = null,
    projectId = null,
    articleId = null,
    blogId = null,
    updateHistory = true
  ) => {
    const previousView = currentViewRef.current;

    if (ADMIN_VIEWS.has(previousView) && !ADMIN_VIEWS.has(viewId)) {
      const url = getViewUrl(viewId, projectId, articleId, blogId);
      window.location.hash = url;
      window.location.reload();
      return;
    }

    if (!ADMIN_VIEWS.has(viewId) && viewId !== 'project-detail' && viewId !== 'blog-content') {
      setLastViewBeforeProject(viewId);
    }

    setCurrentView(viewId);
    currentViewRef.current = viewId;
    if (projectId !== null) setSelectedProjectId(projectId);
    if (articleId !== null) setSelectedArticleId(articleId);
    if (blogId !== null) setSelectedBlogId(blogId);
    if (viewId === 'admin_blog_form' && blogId === null && updateHistory) {
      setSelectedBlogId(null);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (updateHistory) {
      window.location.hash = getViewUrl(viewId, projectId, articleId, blogId);
    }

    if (!ADMIN_VIEWS.has(viewId)) {
      const sidebar = getSidebarStateForView(viewId);
      setSidebarLabel(sidebar.sidebarLabel);
      setScrollCounter(sidebar.scrollCounter);
    }
  };

  useEffect(() => {
    const loadViewFromHash = () => {
      const route = parseHashToRoute();
      showView(route.viewId, null, route.projectId, route.articleId, route.blogId, false);
    };

    window.addEventListener('hashchange', loadViewFromHash);
    return () => window.removeEventListener('hashchange', loadViewFromHash);
  }, []);

  const handleNavigate = (view, sectionId = null, projectId = null, articleId = null, blogId = null) => {
    showView(view, sectionId, projectId, articleId, blogId);
  };

  const handleLogoClick = () => {
    showView('home');
  };

  const handleBackFromProject = () => {
    showView(lastViewBeforeProject);
  };

  const handleBackFromArticle = () => {
    showView('blog');
  };

  const handleProjectClick = (projectId) => {
    showView('project-detail', null, projectId);
  };

  const handleArticleClick = (articleId) => {
    showView('blog-content', null, null, articleId);
  };

  const handleAdminNavigate = (viewId, blogId = null) => {
    if (!isAdminAuthenticated() && viewId !== 'admin_login') {
      showView('admin_login');
      return;
    }
    showView(viewId, null, null, null, blogId);
  };

  const handleAdminLoginSuccess = () => {
    showView('admin_dashboard');
  };

  const handleAdminLogout = () => {
    adminLogout();
    showView('admin_login');
  };

  if (ADMIN_VIEWS.has(currentView)) {
    return (
      <Suspense fallback={<div className="text-white p-5">Loading admin...</div>}>
        <AdminApp
          currentView={currentView}
          selectedBlogId={selectedBlogId}
          onLoginSuccess={handleAdminLoginSuccess}
          onNavigate={handleAdminNavigate}
          onLogout={handleAdminLogout}
        />
      </Suspense>
    );
  }

  return (
    <div className="App">
      <SidebarNew
        scrollCounter={scrollCounter}
        sidebarLabel={sidebarLabel}
        onLogoClick={handleLogoClick}
      />

      <main className="lg:ml-[100px]">
        <NavbarNew
          onNavigate={handleNavigate}
          currentView={currentView}
          onLogoClick={handleLogoClick}
        />

        <div className={currentView === 'home' ? '' : 'hidden'}>
          <HomePageNew
            onNavigate={handleNavigate}
            setScrollCounter={setScrollCounter}
          />
        </div>

        <div className={currentView === 'about_page' ? '' : 'hidden'}>
          <AboutPageNew />
        </div>

        <div className={currentView === 'projects_page' ? '' : 'hidden'}>
          <ProjectsPageNew onProjectClick={handleProjectClick} />
        </div>

        <div className={currentView === 'blog' ? '' : 'hidden'}>
          <BlogPageNew onArticleClick={handleArticleClick} isActive={currentView === 'blog'} />
        </div>

        <div className={currentView === 'blog-content' ? '' : 'hidden'}>
          <BlogContentPageNew
            articleId={selectedArticleId}
            onBack={handleBackFromArticle}
            showView={showView}
          />
        </div>

        <div className={currentView === 'project-detail' ? '' : 'hidden'}>
          <ProjectDetailPageNew
            key={selectedProjectId}
            projectId={selectedProjectId}
            onBack={handleBackFromProject}
          />
        </div>

        <div className={currentView === 'clients_page' ? '' : 'hidden'}>
          <ClientsPageNew />
        </div>

        <div className={currentView === 'ventures_page' ? '' : 'hidden'}>
          <OtherVenturesPageNew />
        </div>

        <div className={currentView === 'contact_page' ? '' : 'hidden'}>
          <ContactPageNew />
        </div>
        <FooterNew onNavigate={handleNavigate} />
      </main>
    </div>
  );
}

export default App;
